import React, { useState, useEffect } from "react";
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import TextInput from "../../components/textInput";
import CustomButton from "../../components/customButton";
import useWorkerStore from "../../zustand/useWorkerStore";
import CustomSelect from "../../components/customSelect";
import CustomMultiSelect from "../../components/customMultiSelect";
import { useParams, useNavigate } from "react-router-dom";
import {
  createNotification,
  updateNotification,
  getNotificationById,
} from "../../services/Notification";
import { openSnackAlert } from "../../helper";
import AppLoader from "../../components/appLoader";
const AddEditForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState({
    title: "",
    body: "",
    status: "published",
  });
  const { allWorkers, initializeData } = useWorkerStore();
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initialize workers data on component mount
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Load notification data for edit mode
  useEffect(() => {
    if (isEditMode && id && allWorkers.length > 0) {
      loadNotificationData();
    } else if (!isEditMode) {
      // Reset form when switching to create mode
      setForm({
        title: "",
        body: "",
        status: "published",
      });
      setSelectedWorkers([]);
    }
  }, [id, isEditMode, allWorkers]);

  const loadNotificationData = async () => {
    try {
      setLoading(true);

      const response = await getNotificationById(id);

      if (response?.status === 200 || response?.status === 201) {
        const notification = response.data?.data || response.data;

        setForm({
          title: notification?.title || "",
          body: notification?.body || "",
          status: notification?.status || "published",
        });

        const mappedWorkers = (notification?.workers || [])
          .map((workerData) => {
            // Check if workerData is an object (full worker data) or just an ID
            const workerId =
              typeof workerData === "object" ? workerData._id : workerData;
            const worker = allWorkers.find((w) => w._id === workerId);
            return worker
              ? {
                  value: worker._id,
                  label: `${worker.firstName} ${worker.lastName}`,
                }
              : null;
          })
          .filter(Boolean);

        setSelectedWorkers(mappedWorkers);
      } else {
        openSnackAlert("Failed to load notification data", "error");
      }
    } catch (error) {
      console.error("Error loading notification:", error);
      openSnackAlert("Error loading notification data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWorkerChange = (selectedOptions) => {
    setSelectedWorkers(selectedOptions || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.body.trim()) {
      openSnackAlert("Please fill in all required fields", "error");
      return;
    }

    if (selectedWorkers.length === 0) {
      openSnackAlert("Please select at least one worker", "error");
      return;
    }

    try {
      setLoading(true);



      const payload = {
        title: form.title,
        body: form.body,
        workers: selectedWorkers.map((worker) => worker.value),
        status: form.status,
      };

      let response;
      if (isEditMode) {
        response = await updateNotification(id, payload);
      } else {
        response = await createNotification(payload);
      }

      if (response?.status === 200 || response?.status === 201) {
        openSnackAlert(
          `Notification ${isEditMode ? "updated" : "created"} successfully`,
          "success"
        );
        navigate("/announcements");
      }
    } catch (error) {
      console.error("Error saving notification:", error);
      openSnackAlert("Error saving notification", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={loading} />
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <Typography variant="h4">
            {isEditMode ? "Edit Announcement" : "Add Announcement"}
          </Typography>
          <Typography variant="description">Keep team updated</Typography>
        </Grid>
      </Grid>

      <Box display="flex" width={"100%"} flexDirection="column" gap={2} py={2}>
        <Box sx={{ mb: 2 }}>
          <CustomMultiSelect
            label="Select Workers"
            placeholder="Select Workers"
            value={selectedWorkers.map(worker => worker.value)}
            onChange={(values) => {
              const selectedWorkerObjects = values.map(value => 
                allWorkers.find(worker => worker._id === value)
              ).filter(Boolean).map(worker => ({
                value: worker._id,
                label: `${worker.firstName} ${worker.lastName}`
              }));
              setSelectedWorkers(selectedWorkerObjects);
            }}
            options={allWorkers.map((worker) => ({
              value: worker._id,
              label: `${worker.firstName} ${worker.lastName}`,
            }))}
          />
        </Box>

        <TextInput
          label="Title"
          name="title"
          value={form.title}
          onChange={handleChange}
          placeholder="Brandon Team"
          fullWidth
          inputBgColor={"#fff"}
          borderColor={"#4CAF50"}
        />

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Write The Announcement
        </Typography>
        <TextInput
          label=""
          name="body"
          value={form.body}
          onChange={handleChange}
          placeholder="Write the announcement"
          fullWidth
          multiline
          rows={5}
          inputBgColor={"#fff"}
          borderColor={"#4CAF50"}
        />

        <CustomSelect
          name="status"
          label="Status"
          value={form.status}
          onChange={handleChange}
          sx={{ width: "100%", height: "36px" }}
          InputWidth="40%"
        >
          <MenuItem value="published">Published</MenuItem>
          <MenuItem value="unpublished">Unpublished</MenuItem>
        </CustomSelect>


        
      </Box>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={6}>
        <CustomButton
          type="submit"
          handlePressBtn={handleSubmit}
          btnLabel={isEditMode ? "Update" : "Publish"}
          btnBgColor="#fff"
          btnTextColor="#225B27"
          borderColor="#225B27"
          isBorder="1px solid #225B27"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
          disabled={loading}
        />
        <CustomButton
          btnLabel="Cancel"
          btnBgColor="#E0E0E0"
          btnTextColor="#222"
          width={100}
          sx={{ boxShadow: 0, borderRadius: "22px" }}
          handlePressBtn={() => navigate("/announcements")}
          disabled={loading}
        />
      </Box>
    </Box>
  );
};

export default AddEditForm;
